// The last line of defence between a render bug and a black screen.
//
// React unmounts the entire tree when a render throws, and with nothing above
// it that means the admin becomes an empty page with no message and no way
// back -- which is exactly how a one-line type mismatch in the products field
// read as "the panel is broken, reload and hope".
//
// It catches render errors only. Failures inside event handlers and promises
// never reach it; those already report through the toast channel.

import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';

export class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The stack is the only record of what happened -- there is no error
    // reporting service behind this.
    console.error('Admin crashed while rendering:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Box sx={{ p: '22px', maxWidth: 720 }}>
        <Alert severity="error">
          <AlertTitle>The admin panel hit a bug and stopped rendering</AlertTitle>
          Nothing was lost on the server — no save is in flight when this happens. Unsaved
          edits in the form are gone.
          <Typography
            className="admin-mono"
            sx={{ fontSize: 11, mt: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {String(error?.message || error)}
          </Typography>
        </Alert>
        <Box sx={{ display: 'flex', gap: '4px', mt: '18px' }}>
          {/* Dismiss re-renders the same tree: worth a try when the crash came
              from one dialog, useless when the catalog itself is the problem,
              which is what Reload is for. */}
          <Button variant="contained" onClick={() => this.setState({ error: null })}>
            Dismiss and try again
          </Button>
          <Button onClick={() => window.location.reload()}>Reload the page</Button>
        </Box>
      </Box>
    );
  }
}

export default AdminErrorBoundary;
